import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, UserPlus, Users } from 'lucide-react';
import { api, LitLegionNpc } from '@/lib/api';

interface QuestGiversProps {
  /** Called after an NPC assignment added at least one new quest. */
  onAssigned: () => void;
}

/**
 * Quest-giver gallery: lets the player "meet" an NPC, which assigns the
 * NPC's root quests (POST /assign-npc — idempotent, so re-meeting is safe
 * and reports zero new quests).
 */
export function QuestGivers({ onAssigned }: QuestGiversProps) {
  const [npcs, setNpcs] = useState<LitLegionNpc[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listLitLegionNpcs()
      .then((result) => {
        if (!cancelled) setNpcs(result.npcs);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load NPCs');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleMeet = async (npc: LitLegionNpc) => {
    setAssigningId(npc.npcId);
    setFeedback(null);
    setError(null);
    try {
      const result = await api.assignLitLegionNpc(npc.npcId);
      if (result.count > 0) {
        setFeedback(
          `${npc.name} gave you ${result.count} new quest${result.count === 1 ? '' : 's'}!`,
        );
        onAssigned();
      } else {
        setFeedback(`You already have every quest ${npc.name} offers right now.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start quests');
    } finally {
      setAssigningId(null);
    }
  };

  if (loading || npcs.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4" /> Quest Givers
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Meet a quest giver to start their quest line.
        </p>
        <div className="flex flex-wrap gap-2">
          {npcs.map((npc) => (
            <Button
              key={npc.npcId}
              variant="outline"
              size="sm"
              onClick={() => handleMeet(npc)}
              disabled={assigningId !== null}
            >
              {assigningId === npc.npcId ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <UserPlus className="h-3.5 w-3.5 mr-1.5" />
              )}
              {npc.name}
            </Button>
          ))}
        </div>
        {feedback && <p className="text-sm text-muted-foreground">{feedback}</p>}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}
